import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { USERS_TAB_ENUM } from "./constants";

import {
  CreateDictionariesRoleModel,
  DictionariesRoleModel,
  UpdateDictionariesRoleModel,
} from "../../../viewModels/Dictionaries/DictionariesRoleModel";
import { BasicUserRoleModel } from "../../../viewModels/User/UserInfoModel";
import { FormErrorModel } from "../../../viewModels/General/ErrorModel";
import {
  CreateDictionariesUserModel,
  DictionariesUserModel,
  UpdateDictionariesUserModel,
} from "../../../viewModels/Dictionaries/DictionariesUserModel";
import SortObject from "../../../types/SortObject";

const initialState: {
  loading: boolean;
  loadingData: boolean;
  activeUsersTabId: string;

  internalUsers: Array<DictionariesUserModel>;
  roles: Array<DictionariesRoleModel>;

  total: number;
  pageSize: number;
  page: number;
  sort: SortObject;
  searchFilterValue: string;

  internalUserIdToEdit: number;
  isEditInternalUserDrawerOpen: boolean;
  isEditInternalUserDrawerLoading: boolean;
  createUserFormErrors: Array<FormErrorModel>;

  externalUserIdToEdit: number;
  isEditExternalUserDrawerOpen: boolean;
  isEditExternalUserDrawerLoading: boolean;

  roleIdToEdit: number;
  isEditRoleDrawerOpen: boolean;
  isEditRoleDrawerLoading: boolean;

  rolesBasicInfo: Array<BasicUserRoleModel>;
} = {
  loading: true,
  loadingData: true,

  activeUsersTabId: undefined!,

  internalUsers: undefined!,
  roles: undefined!,

  total: undefined!,
  pageSize: 10,
  page: 1,

  sort: undefined!,
  searchFilterValue: "",

  internalUserIdToEdit: undefined!,
  isEditInternalUserDrawerLoading: false,
  isEditInternalUserDrawerOpen: false,
  createUserFormErrors: undefined!,

  externalUserIdToEdit: undefined!,
  isEditExternalUserDrawerLoading: false,
  isEditExternalUserDrawerOpen: false,

  roleIdToEdit: undefined!,
  isEditRoleDrawerLoading: false,
  isEditRoleDrawerOpen: false,

  rolesBasicInfo: undefined!,
};

const dictionariesUsersSlice = createSlice({
  name: "dictionariesUsers",
  initialState,
  reducers: {
    loadInternalUsers(state) {
      state.loadingData = true;
      state.activeUsersTabId = USERS_TAB_ENUM.USERS;
      state.page = 1;
    },
    loadRoles(state) {
      state.loadingData = true;
      state.activeUsersTabId = USERS_TAB_ENUM.ROLES;
      state.page = 1;
    },
    setLoadedInternalUsers(state, action: PayloadAction<{ users: Array<DictionariesUserModel>; total: number }>) {
      const { users, total } = action.payload;
      state.loading = false;
      state.loadingData = false;
      state.internalUsers = users;
      state.total = total;
    },
    setLoadedRoles(state, action: PayloadAction<{ roles: Array<DictionariesRoleModel>; total: number }>) {
      const { roles, total } = action.payload;
      state.loading = false;
      state.loadingData = false;
      state.roles = roles;
      state.total = total;
    },
    changeTable(state, action: PayloadAction<{ page: number; pageSize: number; sort: SortObject }>) {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.sort = {
        key: action.payload.sort.key,
        order: action.payload.sort.order,
      };
      state.loadingData = true;
    },

    changeSearchFilterValue(state, action: PayloadAction<string>) {
      state.searchFilterValue = action.payload;
    },
    search(state) {
      state.loadingData = true;
      state.page = 1;
    },
    openEditInternalUserDrawer(state, action: PayloadAction<number | undefined>) {
      state.internalUserIdToEdit = action.payload!;
      state.isEditInternalUserDrawerOpen = true;
    },
    closeEditInternalUserDrawer(state) {
      if (!state.isEditInternalUserDrawerLoading) {
        state.internalUserIdToEdit = undefined!;
        state.isEditInternalUserDrawerOpen = false;
      }
    },

    createUser(state, _action: PayloadAction<CreateDictionariesUserModel>) {
      state.isEditInternalUserDrawerLoading = true;
    },
    createUserSuccess(state) {
      state.isEditInternalUserDrawerLoading = false;
      state.isEditInternalUserDrawerOpen = false;
    },
    createUserError(state, action: PayloadAction<any>) {
      if (action.payload.find((error: FormErrorModel) => error.field === "email")) {
        state.createUserFormErrors = action.payload;
      } else {
        state.isEditInternalUserDrawerOpen = false;
      }
      state.isEditInternalUserDrawerLoading = false;
    },

    updateUser(state, _action: PayloadAction<UpdateDictionariesUserModel>) {
      state.isEditInternalUserDrawerLoading = true;
    },
    updateUserSuccess(state) {
      state.isEditInternalUserDrawerLoading = false;
      state.isEditInternalUserDrawerOpen = false;
    },
    updateUserError(state) {
      state.isEditInternalUserDrawerLoading = false;
      state.isEditInternalUserDrawerOpen = false;
    },

    resetUserPassword(state, _action: PayloadAction<number>) {
      state.isEditInternalUserDrawerLoading = true;
    },
    resetUserPasswordSuccess(state) {
      state.isEditInternalUserDrawerLoading = false;
    },
    resetUserPasswordError(state) {
      state.isEditInternalUserDrawerLoading = false;
    },

    openEditExternalUserDrawer(state, action: PayloadAction<number | undefined>) {
      state.externalUserIdToEdit = action.payload!;
      state.isEditExternalUserDrawerOpen = true;
    },
    closeEditExternalUserDrawer(state) {
      if (!state.isEditExternalUserDrawerLoading) {
        state.externalUserIdToEdit = undefined!;
        state.isEditExternalUserDrawerOpen = false;
      }
    },
    openEditRoleDrawer(state, action: PayloadAction<number | undefined>) {
      state.roleIdToEdit = action.payload!;
      state.isEditRoleDrawerOpen = true;
    },
    closeEditRoleDrawer(state) {
      if (!state.isEditRoleDrawerLoading) {
        state.roleIdToEdit = undefined!;
        state.isEditRoleDrawerOpen = false;
      }
    },

    createRole(state, _action: PayloadAction<CreateDictionariesRoleModel>) {
      state.isEditRoleDrawerLoading = true;
    },
    createRoleSuccess(state) {
      state.isEditRoleDrawerLoading = false;
      state.isEditRoleDrawerOpen = false;
    },
    createRoleError(state) {
      state.isEditRoleDrawerLoading = false;
      state.isEditRoleDrawerOpen = false;
    },

    updateRole(state, _action: PayloadAction<UpdateDictionariesRoleModel>) {
      state.isEditRoleDrawerLoading = true;
    },
    updateRoleSuccess(state) {
      state.isEditRoleDrawerLoading = false;
      state.isEditRoleDrawerOpen = false;
    },
    updateRoleError(state) {
      state.isEditRoleDrawerLoading = false;
      state.isEditRoleDrawerOpen = false;
    },

    changeActiveUsersTabId(state, action: PayloadAction<string>) {
      state.activeUsersTabId = action.payload;
    },

    loadRolesBasicInfo() {},
    loadRolesBasicInfoSuccess(state, action: PayloadAction<Array<BasicUserRoleModel>>) {
      state.rolesBasicInfo = action.payload;
    },
  },
});

export default dictionariesUsersSlice;
